import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { useObservableAndState } from '@provider-portal/common/hooks/useObservableAndState';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import {
  BOLT_STAR_HOW_IT_WORKS_ROUTE,
  BOLT_STAR_ROUTE,
  OnboardingPopupVariant,
  useBoltStarMonthlyUpdate,
} from '@provider-portal/mp2-common/topPerformer';

import { selectIsAccountOrSelectedProviderOnboarding } from 'rdx/account/selectors';

import { BoltStarMonthlyUpdateDialogContainer } from 'widgets/BoltStarMonthlyUpdateDialogContainer/BoltStarMonthlyUpdateDialogContainer';
import { BoltStarOnboardingDialogContainer } from 'widgets/BoltStarOnboardingDialogContainer/BoltStarOnboardingDialogContainer';
import { dismissAllOnboardingDialogs } from 'widgets/BoltStarOnboardingDialogContainer/onboardingDialogDismissals';

/**
 * Picks at most one Bolt Star dialog to show. A monthly update outranks the onboarding intros and
 * marks them as seen, so a merchant never gets both. Opening the How it works page also marks the
 * onboarding intros as seen.
 */
export function BoltStarDialogs() {
  const { accountService } = useCommonAppServices();
  const { pathname } = useLocation();

  const { providerId } = useObservableAndState(accountService.getSelectedProviderData$());
  const isOnboarding = useSelector(selectIsAccountOrSelectedProviderOnboarding);

  const isProviderSuitable = providerId && !isOnboarding;

  const {
    update,
    isTopPerformerEnabled: isBoltStarEnabled,
    isLoading: isMonthlyReportLoading,
  } = useBoltStarMonthlyUpdate(isProviderSuitable ? providerId : undefined);

  const allowShowingDialogs = isProviderSuitable && !isMonthlyReportLoading && isBoltStarEnabled;

  const isOnHowItWorksPage = Boolean(
    matchPath(pathname, { path: BOLT_STAR_HOW_IT_WORKS_ROUTE, exact: true }),
  );
  const isOnBoltStarPage = Boolean(matchPath(pathname, { path: BOLT_STAR_ROUTE, exact: true }));

  useEffect(() => {
    if (isOnHowItWorksPage) {
      dismissAllOnboardingDialogs();
    }
  }, [isOnHowItWorksPage]);

  useEffect(() => {
    if (allowShowingDialogs && update) {
      dismissAllOnboardingDialogs();
    }
  }, [allowShowingDialogs, update]);

  if (!allowShowingDialogs) {
    return null;
  }

  if (update) {
    return <BoltStarMonthlyUpdateDialogContainer update={update} providerId={providerId} />;
  }

  return (
    <>
      <BoltStarOnboardingDialogContainer
        variant={OnboardingPopupVariant.DEFAULT}
        isActive={!isOnBoltStarPage && !isOnHowItWorksPage}
      />
      <BoltStarOnboardingDialogContainer
        variant={OnboardingPopupVariant.BOLT_STAR_PAGE}
        isActive={isOnBoltStarPage}
      />
    </>
  );
}
